import { scrollToElementAfterTimeout } from "@faire/help-center-utils/lib/window";
import { isNotUndefined } from "@faire/typescript";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { useState } from "@faire/web--source/ui/hooks/useState";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import React, { useEffect, useLayoutEffect, useRef } from "react";

import { useAsyncDataStatus } from "../hooks/useAsyncDataStatus";
import { useNodesConditionalVisibilityState } from "../hooks/useNodesConditionalVisibilityState";
import { usePeteEventAdditionalDataEffect } from "../hooks/usePeteEventAdditionalDataEffect";
import { IssueId, SelfServeContext, SelfServeNode } from "../types";
import { getDefaultOtherNodeOrUndefined } from "../utils/getDefaultOtherNodeOrUndefined";
import { getNodesThatCanBeRendered } from "../utils/getNodesThatCanBeRendered";

import { CenteredLoadingSpinner } from "./CenteredLoadingSpinner";
import { IssueTopicCard } from "./IssueTopicCard/IssueTopicCard";
import { OtherIssueTopicCard } from "./OtherIssueTopicCard";

const ONE_HUNDRED_MILLISECONDS = 100;

interface IProps {
  context: SelfServeContext;
  node: SelfServeNode | undefined;
  selectedNode: SelfServeNode | undefined;
  onClickOnNode: (node: SelfServeNode) => void;
  isAutofocusOnMobile?: boolean;
  bottomElementRef?: React.MutableRefObject<HTMLElement | null>;
}

/**
 * General purpose component to render the children of a node
 */
export const NodeChildrenRenderer: React.FC<IProps> = ({
  context,
  node,
  selectedNode,
  onClickOnNode,
  isAutofocusOnMobile,
  bottomElementRef,
}) => {
  const { isMobile } = useViewport();
  const { strictLocalize } = useStrictLocalization();

  const {
    asyncDataInProgress,
    asyncDataComplete,
    setAsInProgress,
    setAsComplete,
    setAsNotRequestedYet,
  } = useAsyncDataStatus();
  const { nodesConditionalVisibility, updateNodeVisibility } =
    useNodesConditionalVisibilityState();

  usePeteEventAdditionalDataEffect(context, selectedNode);

  useEffect(() => {
    setAsNotRequestedYet();
  }, [node, setAsNotRequestedYet]);

  useEffect(() => {
    if (node && node.children && !asyncDataInProgress && !asyncDataComplete) {
      setAsInProgress();
      callVisibilityFunctions(
        context,
        node.children,
        updateNodeVisibility
      ).then(setAsComplete);
    }
  }, [
    node,
    context,
    asyncDataInProgress,
    asyncDataComplete,
    setAsComplete,
    setAsInProgress,
    updateNodeVisibility,
  ]);

  const nodesThatShouldBeRendered = getNodesThatCanBeRendered(
    context.tree,
    node?.children ?? []
  ).filter((node) =>
    filterOutNodesThatAreNotVisible(node, nodesConditionalVisibility)
  );

  const issueTopicCardRefs = useRef(
    nodesThatShouldBeRendered.map(() => React.createRef<HTMLButtonElement>())
  );
  const [maxHeight, setMaxHeight] = useState<number>(94);
  useLayoutEffect(() => {
    const heights = issueTopicCardRefs.current.map(
      (ref) => ref.current?.offsetHeight ?? 0
    );
    const maxHeight = Math.max(...heights);
    setMaxHeight(maxHeight);
  }, [nodesThatShouldBeRendered]);

  if (!node || !node.children) {
    return null;
  }

  if (!asyncDataComplete) {
    return <CenteredLoadingSpinner />;
  }

  const handleNodeClick = async (node: SelfServeNode) => {
    onClickOnNode(node);
    // Assign setting for experiment when nodes are clicked
    if (node.assignSetting) {
      await node.assignSetting();
    }
    if (isAutofocusOnMobile && isMobile) {
      scrollToElementAfterTimeout(
        bottomElementRef?.current,
        ONE_HUNDRED_MILLISECONDS
      );
    }
  };

  const otherNode = node.shouldStopAddingDefaultOtherNodeToChildren
    ? undefined
    : getDefaultOtherNodeOrUndefined(nodesThatShouldBeRendered, strictLocalize);

  return (
    <>
      {nodesThatShouldBeRendered.map((node, index) => (
        <IssueTopicCard
          key={node.id}
          text={node.label!}
          icon={node.icon}
          isSelected={node.id === selectedNode?.id}
          onClick={() => handleNodeClick(node)}
          dataTestId={`self-serve-node-${node.id}`}
          buttonRef={issueTopicCardRefs.current[index]}
          style={{
            minHeight: isMobile ? `${maxHeight}px` : "auto",
          }}
        />
      ))}

      {otherNode ? (
        <OtherIssueTopicCard
          otherNode={otherNode}
          selectedNode={selectedNode}
          parentNode={node}
          onClick={handleNodeClick}
          style={{
            minHeight: isMobile ? `${maxHeight}px` : "auto",
          }}
        />
      ) : null}
    </>
  );
};

const callVisibilityFunctions = (
  context: SelfServeContext,
  children: IssueId[],
  updateNodeVisibility: (node: SelfServeNode, isVisible: boolean) => void
): Promise<boolean[]> => {
  const visibilityPromises = getNodesThatCanBeRendered(context.tree, children)
    .filter((node) => isNotUndefined(node.isNodeVisible))
    .map((node) => {
      return node.isNodeVisible!(context).then((isVisible) => {
        updateNodeVisibility(node, isVisible);
        return isVisible;
      });
    });

  return Promise.all(visibilityPromises);
};

/**
 * Filters out nodes based on the nodeVisibility state property value for the node
 */
export const filterOutNodesThatAreNotVisible = (
  node: SelfServeNode,
  nodesConditionalVisibility: Readonly<Record<string, boolean>>
): boolean => {
  const nodeVisibility = nodesConditionalVisibility[node.id];
  const nodeIsVisibleByDefault = nodeVisibility === undefined;
  const nodeIsVisibleByCalculation = nodeVisibility === true;
  return nodeIsVisibleByDefault || nodeIsVisibleByCalculation;
};
